import { XClose } from '@ethsign/icons';
import { Badge, Input, Textarea } from '@ethsign/ui';
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  size,
  useDismiss,
  useFloating,
  useId,
  useInteractions,
  useListNavigation,
  useRole
} from '@floating-ui/react';
import { concat, isNil } from 'lodash-es';
import React, { forwardRef, ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

interface ItemProps {
  children: React.ReactNode;
  active: boolean;
}

export interface IOption {
  label: ReactNode;
  value: string;
  data?: any;
}

export interface IAutoCompleteProps {
  options: Array<IOption>;
  emptyText?: string;
  placeholder?: string;
  className?: string;
  value?: any[];
  onChange?: (v: any[], item?: IOption) => void;
  onSearch?: (v: string) => void;
  filterOption?: (inputValue: string, option: IOption) => boolean;
  multiple?: boolean;
  renderTag?: (item: any) => ReactNode;
  formatValue?: (item: any) => any;
  onSearchValueChange?: (v: string) => void;
  searchValue?: string;
  inputType?: 'Input' | 'Textarea';
}

export interface IAutoCompleteSingleProps {
  options: Array<IOption>;
  emptyText?: string;
  placeholder?: string;
  className?: string;
  value?: string;
  onChange?: (v: string, item?: IOption) => void;
  onSearch?: (v: string) => void;
  filterOption?: (inputValue: string, option: IOption) => boolean;
}

// eslint-disable-next-line react/display-name
const Item = forwardRef<HTMLDivElement, ItemProps & React.HTMLProps<HTMLDivElement>>(
  ({ children, active, ...rest }, ref) => {
    const id = useId();
    return (
      <div
        ref={ref}
        role="option"
        id={id}
        aria-selected={active}
        {...rest}
        className={cn(
          'text-xs text-gray-900 cursor-pointer px-[14px] py-[10px] focus:bg-accent hover:bg-gray-50 focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
          active ? 'bg-accent' : ''
        )}
      >
        {children}
      </div>
    );
  }
);

export function AutoComplete({
  options,
  onSearch,
  filterOption,
  value,
  onChange,
  placeholder,
  formatValue,
  renderTag,
  onSearchValueChange,
  searchValue: searchVal,
  inputType
}: IAutoCompleteProps) {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState(searchVal || '');
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const listRef = useRef<Array<HTMLElement | null>>([]);

  const { refs, floatingStyles, context } = useFloating<HTMLInputElement>({
    whileElementsMounted: autoUpdate,
    open,
    onOpenChange: setOpen,
    middleware: [
      flip({ padding: 10 }),
      size({
        apply({ rects, availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
            maxHeight: `${availableHeight}px`
          });
        },
        padding: 10
      })
    ]
  });

  const role = useRole(context, { role: 'listbox' });
  const dismiss = useDismiss(context);
  const listNav = useListNavigation(context, {
    listRef,
    activeIndex,
    onNavigate: setActiveIndex,
    virtual: true,
    loop: true
  });

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([role, dismiss, listNav]);

  function onSearchChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const v = event.target.value;
    if (v.includes(',')) {
      const values = v.split(',').filter(Boolean);
      onChange?.(
        concat(
          value,
          values?.map((v) => formatValue?.(v))
        )
      );
      onSearchValueChange?.('');
      return;
    }
    onSearchValueChange?.(v);

    if (v) {
      onSearch?.(v);
      setOpen(true);
      setActiveIndex(0);
    } else {
      setOpen(false);
    }
  }

  useEffect(() => {
    setSearchValue(searchVal || '');
  }, [searchVal]);

  const handleFocus = () => {
    if (!onSearch) {
      setOpen(true);
    }
  };

  const items = useMemo(
    () =>
      options.filter((item) => {
        return filterOption ? filterOption(searchValue, item) : true;
      }),
    [options]
  );

  useEffect(() => {
    if (open && items?.length) {
      setActiveIndex(0);
    }
  }, [items, open]);

  const current = useMemo(() => {
    return items.filter((it) => value?.find((v) => it.data === v));
  }, [value, items]);

  const inputProps = {
    className: 'border-none shadow-none min-w-[10px]',
    onChange: onSearchChange,
    onClick: handleFocus,
    value: searchValue,
    placeholder: placeholder,
    'aria-autocomplete': 'list' as const,
    onKeyDown(event: any) {
      // delete
      if (event.keyCode === 8) {
        console.log('delete');
        if (value?.length && !searchValue) {
          // delete
          if (value?.[value.length - 1]?.status === 'selected') {
            onChange?.(value?.slice(0, value.length - 1));
          } else {
            // select
            onChange?.(
              value?.map((it, i) => {
                if (i === value.length - 1) {
                  return {
                    ...it,
                    status: 'selected'
                  };
                }
                return it;
              })
            );
          }
        }
      }
      if (event.key === 'Enter') {
        console.log(searchValue, 'enter');
        // 有options，且有activeIndex，键盘上下切换更新activeIndex，enter选中直接提交
        if (open && !isNil(activeIndex) && items[activeIndex]) {
          onChange?.(concat(value, items[activeIndex].data));
          setActiveIndex(null);
          setOpen(false);
          onSearchValueChange?.('');
        } else {
          if (searchValue) {
            onChange?.(concat(value, formatValue ? formatValue(searchValue) : searchValue));
            onSearchValueChange?.('');
          }
        }
      }
    }
  };

  return (
    <>
      <div
        className="flex flex-wrap w-full rounded-md shadow-xs border border-input p-1"
        {...getReferenceProps({
          ref: refs.setReference
        })}
      >
        <div className={'flex flex-wrap gap-[6px] items-center'}>
          {value?.length
            ? value?.map((it, i) => {
                return (
                  <Badge
                    variant="outline"
                    className={cn(
                      'rounded-md text-sm text-gray-700',
                      it.status === 'selected' ? 'bg-blue-500 text-white border-0' : '',
                      it.status === 'error' ? 'bg-red-500 text-white border-0' : ''
                    )}
                    key={i}
                  >
                    <span>{renderTag ? renderTag(it) : it.label}</span>
                    <span
                      className={'ml-[2px] cursor-pointer'}
                      onClick={() => {
                        onChange?.(value?.filter((item) => item !== it));
                      }}
                    >
                      <XClose color={['selected', 'error'].includes(it.status) ? '#FFF' : '#98A2B3'} size={16} />
                    </span>
                  </Badge>
                );
              })
            : null}
        </div>
        <div className="flex-1 min-w-[200px]">
          {inputType === 'Textarea' ? <Textarea {...inputProps} /> : <Input {...inputProps} />}
        </div>
      </div>
      <FloatingPortal>
        {open && (
          <FloatingFocusManager context={context} initialFocus={-1} visuallyHiddenDismiss>
            {items.length ? (
              <div
                {...getFloatingProps({
                  ref: refs.setFloating,
                  style: {
                    ...floatingStyles
                  },
                  className: 'bg-popover overflow-y-auto shadow-md border rounded-md'
                })}
              >
                {items.map((item, index) => {
                  return (
                    <Item
                      key={index}
                      {...getItemProps({
                        ref(node) {
                          listRef.current[index] = node;
                        },
                        onClick() {
                          onChange?.(concat(value, item.data), item);
                          onSearchValueChange?.('');
                          setOpen(false);
                          refs.domReference.current?.focus();
                        }
                      })}
                      active={activeIndex === index || !!current.find((it) => it.data === item.data)}
                    >
                      {item.label}
                    </Item>
                  );
                })}
              </div>
            ) : (
              <></>
            )}
          </FloatingFocusManager>
        )}
      </FloatingPortal>
    </>
  );
}

export function AutoCompleteOfSingle({
  options,
  onSearch,
  filterOption,
  value,
  onChange,
  placeholder,
  className
}: IAutoCompleteSingleProps) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const listRef = useRef<Array<HTMLElement | null>>([]);

  const { refs, floatingStyles, context } = useFloating<HTMLInputElement>({
    whileElementsMounted: autoUpdate,
    open,
    onOpenChange: setOpen,
    middleware: [
      flip({ padding: 10 }),
      size({
        apply({ rects, availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
            maxHeight: `${availableHeight}px`
          });
        },
        padding: 10
      })
    ]
  });

  const role = useRole(context, { role: 'listbox' });
  const dismiss = useDismiss(context);
  const listNav = useListNavigation(context, {
    listRef,
    activeIndex,
    onNavigate: setActiveIndex,
    virtual: true,
    loop: true
  });

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([role, dismiss, listNav]);

  function onSearchChange(event: React.ChangeEvent<HTMLInputElement>) {
    const v = event.target.value;

    if (v) {
      onSearch?.(v);
      setOpen(true);
      setActiveIndex(0);
    } else {
      setOpen(false);
    }
    onChange?.(v);
  }

  const handleFocus = () => {
    if (!onSearch) {
      setOpen(true);
    }
  };

  const items = useMemo(
    () =>
      options.filter((item) => {
        return filterOption ? filterOption(value!, item) : true;
      }),
    [options]
  );

  useEffect(() => {
    if (open && items?.length) {
      setActiveIndex(0);
    }
  }, [items, open]);

  const current = useMemo(() => {
    return items.filter((it) => it.value === value);
  }, [value, items]);

  const inputProps = getReferenceProps({
    className,
    ref: refs.setReference,
    onChange: onSearchChange,
    onClick: handleFocus,
    value: value,
    placeholder: placeholder,
    'aria-autocomplete': 'list' as const,
    onKeyDown(event: any) {
      // delete
      if (event.key === 'Enter') {
        // 有options，且有activeIndex，键盘上下切换更新activeIndex，enter选中直接提交
        if (open && !isNil(activeIndex) && items[activeIndex]) {
          onChange?.(items[activeIndex].value, items[activeIndex]);
          setActiveIndex(null);
          setOpen(false);
        }
      }
    }
  });

  return (
    <>
      <Input {...inputProps} />
      <FloatingPortal>
        {open && (
          <FloatingFocusManager context={context} initialFocus={-1} visuallyHiddenDismiss>
            {items.length ? (
              <div
                {...getFloatingProps({
                  ref: refs.setFloating,
                  style: {
                    ...floatingStyles
                  },
                  className: 'bg-popover overflow-y-auto shadow-md border rounded-md'
                })}
              >
                {items.map((item, index) => {
                  return (
                    <Item
                      key={index}
                      {...getItemProps({
                        ref(node) {
                          listRef.current[index] = node;
                        },
                        onClick() {
                          onChange?.(item.value, item);
                          setOpen(false);
                          refs.domReference.current?.focus();
                        }
                      })}
                      active={activeIndex === index || !!current.find((it) => it.data === item.data)}
                    >
                      {item.label}
                    </Item>
                  );
                })}
              </div>
            ) : (
              <></>
            )}
          </FloatingFocusManager>
        )}
      </FloatingPortal>
    </>
  );
}
